import { Injectable } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { StoreHelper } from "@calaosoft/osapp-common/store/helpers/store-helper";
import { IdHelper } from "@calaosoft/osapp-common/utils/helpers/idHelper";
import { EPrefix } from "@calaosoft/osapp-common/utils/models/EPrefix";
import { ModelResolver } from "@calaosoft/osapp-common/utils/models/model-resolver";
import { Observable, throwError } from "rxjs";
import { map, mergeMap, startWith } from "rxjs/operators";
import { IConversation } from "../../../model/conversation/IConversation";
import { InitComponentError } from "../../../model/errors/InitComponentError";
import { ActivePageManager } from "../../../model/navigation/ActivePageManager";
import { IChangeEvent } from "../../../model/store/IChangeEvent";
import { ConversationService } from "../../../services/conversation.service";
import { Store } from "../../../services/store.service";
import { IDataSourceRemoteChanges } from "../../store/model/IDataSourceRemoteChanges";
import { Conversation } from "../model/conversation";
import { DeletedConversationError } from "../model/errors/deleted-conversation-error";
import { IConversationNavigationState } from "../model/iconversation-navigation-state";

@Injectable()
export class ConversationComponentService {
	//#region METHODS

	public constructor(
		private readonly ioRoute: ActivatedRoute,
		private readonly ioRouter: Router,
		private readonly isvcConversation: ConversationService,
		private readonly isvcStore: Store
	) {}

	/** Récupère la conversation courante.
	 * @throws
	 * - `InitComponentError` si la conversation n'a pas été trouvée.
	 * - `DeletedConversationError` si la conversation a été supprimée.
	 */
	public getConversation$(poActivePageManager: ActivePageManager): Observable<Conversation> {
		const lsConvId: string = IdHelper.buildId(EPrefix.conversation, this.ioRoute.snapshot.params.conversationId);

		return this.getLiveConversation$(lsConvId, poActivePageManager).pipe(
			mergeMap((poConversation: Conversation | undefined, pnIndex: number) => {
				if (poConversation) return this.isvcConversation.hydrateConversation$(poConversation);
				// pnIndex: premier passage. Dans les autres cas, c'est des modifications de la requête live.
				else if (pnIndex === 0) return throwError(() => new InitComponentError(`La conversation n'a pas été trouvée.`));
				else return throwError(() => new DeletedConversationError());
			}),
			map((poConversation?: Conversation) => {
				if (poConversation) return poConversation;
				else throw new InitComponentError(`Les données de la conversation n'ont pas pu être chargées.`);
			})
		);
	}

	private getLiveConversation$(
		psConversationId: string,
		poActivePageManager: ActivePageManager
	): Observable<Conversation | undefined> {
		const loConversation: IConversation | undefined = (
			this.ioRouter.currentNavigation()?.extras.state as IConversationNavigationState
		)?.conversation;

		if (loConversation) {
			const loDatasource: IDataSourceRemoteChanges = {
				remoteChanges: true,
				activePageManager: poActivePageManager,
				live: true,
				databaseId: this.isvcConversation.databaseId,
				viewParams: {
					key: loConversation._id,
					include_docs: true
				}
			};

			// On s'abonne aux changements de la conversation.
			return this.isvcStore.getChanges$<IConversation>(loDatasource).pipe(
				map((poConversation: IChangeEvent<IConversation>[]) => poConversation[0]?.document),
				// On émet en valeur initiale la conversation dans l'état de la route.
				startWith(loConversation),
				map((poConversation?: IConversation) => {
					if (poConversation) {
						StoreHelper.updateDocumentCacheData(poConversation, { databaseId: this.isvcConversation.databaseId });
						return ModelResolver.toClass(Conversation, poConversation);
					} else return undefined;
				})
			);
		} else return this.isvcConversation.getConversation(psConversationId, true, poActivePageManager);
	}

	//#endregion METHODS
}
